import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Icons } from '../lib/Icons';

const COOKIE_CONSENT_KEY = 'sen-ed-cookie-consent';

/**
 * Cookie consent banner for GDPR compliance
 * Appears on first visit and stores preference in localStorage
 */
const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    analytics: false,
    functional: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to avoid flash on page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      try {
        const savedPrefs = JSON.parse(consent);
        setPreferences(savedPrefs);
      } catch (e) {
        // Invalid stored value, show banner again
        setShowBanner(true);
      }
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowPreferences(false);
    
    // Here you would typically initialize/disable analytics based on preferences
    if (prefs.analytics) {
      // Enable analytics (placeholder - implement as needed)
      console.log('Analytics enabled');
    }
  };

  const acceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      functional: true,
    });
  };

  const rejectNonEssential = () => {
    saveConsent({
      essential: true,
      analytics: false,
      functional: false,
    });
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Backdrop for preferences modal */}
      {showPreferences && (
        <div 
          className="fixed inset-0 bg-black/50 z-[999]"
          onClick={() => setShowPreferences(false)}
        />
      )}

      {/* Main Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[1000] p-4 sm:p-6">
        <div className="mx-auto max-w-4xl">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {showPreferences ? (
              /* Preferences Panel */
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Cookie Preferences</h3>
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <Icons.XMark className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  {/* Essential Cookies */}
                  <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-900 dark:text-white">Essential Cookies</h4>
                        <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                          Required
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                        These cookies are necessary for the website to function. They enable core functionality 
                        such as security, authentication, and session management.
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="w-12 h-7 bg-green-500 rounded-full flex items-center justify-end px-1">
                        <div className="w-5 h-5 bg-white rounded-full shadow" />
                      </div>
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white">Analytics Cookies</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                        These cookies help us understand how visitors use our website. This data helps us 
                        improve our platform for educators.
                      </p>
                    </div>
                    <button
                      onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                      className="flex-shrink-0"
                    >
                      <div className={`w-12 h-7 rounded-full flex items-center px-1 transition-colors ${
                        preferences.analytics 
                          ? 'bg-indigo-500 justify-end' 
                          : 'bg-gray-300 dark:bg-gray-600 justify-start'
                      }`}>
                        <div className="w-5 h-5 bg-white rounded-full shadow" />
                      </div>
                    </button>
                  </div>

                  {/* Functional Cookies */}
                  <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white">Functional Cookies</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                        These cookies enable enhanced functionality and personalisation, such as remembering 
                        your preferences and settings.
                      </p>
                    </div>
                    <button
                      onClick={() => setPreferences(p => ({ ...p, functional: !p.functional }))}
                      className="flex-shrink-0"
                    >
                      <div className={`w-12 h-7 rounded-full flex items-center px-1 transition-colors ${
                        preferences.functional 
                          ? 'bg-indigo-500 justify-end' 
                          : 'bg-gray-300 dark:bg-gray-600 justify-start'
                      }`}>
                        <div className="w-5 h-5 bg-white rounded-full shadow" />
                      </div>
                    </button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={savePreferences}
                    className="flex-1 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors"
                  >
                    Save Preferences
                  </button>
                  <button
                    onClick={acceptAll}
                    className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-semibold rounded-xl transition-colors"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            ) : (
              /* Main Banner View */
              <div className="p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                    <Icons.Shield className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                      We value your privacy
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      We use cookies to provide essential functionality and, with your consent, to improve our 
                      platform. Read our{' '}
                      <Link to="/privacy" className="text-indigo-600 hover:underline">Privacy Policy</Link>
                      {' '}for more information.
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={acceptAll}
                    className="flex-1 sm:flex-none px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={rejectNonEssential}
                    className="flex-1 sm:flex-none px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-semibold rounded-xl transition-colors"
                  >
                    Essential Only
                  </button>
                  <button
                    onClick={() => setShowPreferences(true)}
                    className="flex-1 sm:flex-none px-6 py-3 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium transition-colors"
                  >
                    Manage Preferences
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;

/**
 * Hook to check cookie consent status
 * Usage: const { hasAnalyticsConsent, hasFunctionalConsent } = useCookieConsent();
 */
export const useCookieConsent = () => {
  const [consent, setConsent] = useState({
    essential: true,
    analytics: false,
    functional: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      try {
        setConsent(JSON.parse(stored));
      } catch (e) {
        // Invalid value, use defaults
      }
    }
  }, []);

  return {
    hasEssentialConsent: consent.essential,
    hasAnalyticsConsent: consent.analytics,
    hasFunctionalConsent: consent.functional,
  };
};
